import React, { useState, useEffect } from 'react';
import { functions } from '../firebaseConfig'; 
import { httpsCallable } from 'firebase/functions';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebaseConfig'; 

function Events() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState({});

  // Current date and time (12:06 PM PDT, June 24, 2025)
  const now = new Date('2025-06-24T12:06:00-07:00');

  // Fetch events from Cloud Function
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const getEvents = httpsCallable(functions, 'getEvents');
        const result = await getEvents();
        const eventsData = result.data.events || [];
        const now = new Date()

        // Validate and process events
        const validEvents = eventsData.map(event => {
          if (!event.id || !event.name || !event.date) {
            console.warn('Invalid event data:', event);
            return null;
          }
          const dateObj = new Date(event.date);
          return {
            id: event.id,
            name: event.name,
            date: event.date,
            description: event.description,
            host: event.host,
            creatorId: event.creatorId,
            imageRef: event.imageRef,
            imageURL: event.imageURL,
            location: event.location,
            dateObj: dateObj,
          };
        }).filter(event => event !== null && !isNaN(event.dateObj.getTime()) && event.dateObj > now); 

        // Sort by date and time
        validEvents.sort((a, b) => a.dateObj - b.dateObj);

        setEvents(validEvents);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching events:', err.message, err.details);
        setError('Failed to load events. Please try again later or check your connection.');
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Fetch image URLs
  useEffect(() => {
    const loadImages = async () => {
      const loadedStates = {};
      for (const event of events) {
        if (event.imageURL) {
          loadedStates[event.id] = event.imageURL;
        } else if (event.imageRef) {
          try {
            const url = await getDownloadURL(ref(storage, event.imageRef));
            loadedStates[event.id] = url;
          } catch (err) {
            console.error(`Error loading image for event ${event.id}:`, err);
            loadedStates[event.id] = null;
          }
        } else {
          loadedStates[event.id] = null;
        }
      }
      setImageLoaded(loadedStates);
    };
    if (events.length > 0) loadImages();
  }, [events]);

  // Toggle event expansion
  const handleEventClick = (event) => {
    setSelectedEvent(selectedEvent?.id === event.id ? null : event);
  };

  // Placeholder image component
  const PlaceholderImage = ({ eventId }) => (
    <div
      style={{
        width: '100px',
        height: '100px',
        backgroundColor: '#ccc',
        borderRadius: '8px',
        marginRight: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: '12px',
        overflow: 'hidden',
      }}
    >
      <span>⏳ Loading ⏳</span>
    </div>
  );

  // Group events by day
  const groupEventsByDay = () => {
    const grouped = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0); 
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    events.forEach(event => {
      const dayStr = event.dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'        
      });
      let header = dayStr;
      if (event.dateObj.toDateString() === today.toDateString()) {
        header = 'Today / ' + dayStr.split(', ')[0];
      } else if (event.dateObj.toDateString() === tomorrow.toDateString()) {
        header = 'Tomorrow / ' + dayStr.split(', ')[0];
      }
      if (!grouped[header]) {
        grouped[header] = [];
      }
      grouped[header].push(event);
    });
    return grouped;
  };

  // Render loading or error state
  if (loading) {
    return (
      <div
        className="body"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}
      >
        <p>⏳ Loading Events ⏳</p>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="body"
        style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}
      >
        <p style={{ color: 'red' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 16px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  const groupedEvents = groupEventsByDay();

  return (
    <div className="body" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {selectedEvent ? (
        // Controlled expanded view of a single event
        <div
          style={{
            padding: '16px',
            maxWidth: '600px', 
            margin: '120px auto 0',          
            position: 'relative',
            zIndex: 0,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ maxHeight: 'inherit', overflow: 'hidden' }}>
            {imageLoaded[selectedEvent.id] ? (
              <div style={{ position: 'relative', width: '100%', height: '350px', borderRadius: '20px', overflow: 'hidden', marginBottom: '16px' }}>
                <img
                  src={imageLoaded[selectedEvent.id]}
                  alt={selectedEvent.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    position: 'absolute',
                    top: 0,
                    left: 0,                    
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    console.error(`Image load failed for event ${selectedEvent.id}`);
                  }}
                />
              </div>
            ) : (
              <PlaceholderImage eventId={selectedEvent.id} style={{ width: '100%', height: '200px' }} />
            )}
            <h2 style={{ padding: '5px', color: '#F48E28', marginBottom: '5px' }}>{selectedEvent.name}</h2>        
            {selectedEvent.host && (
              <p style={{ padding: '1px', color: '#A68368', fontWeight: '700' }}>Host: {selectedEvent.host}</p>
            )}
            {selectedEvent.location.name && (
              <p style={{ padding: '1px', color: '#A68368', fontWeight: '700' }}>Location: {selectedEvent.location.name}</p>
            )}
            <p style={{ whiteSpace: 'pre-wrap', padding: '5px', color: '#6C441A' }} dangerouslySetInnerHTML={{ __html: selectedEvent.description || '' }} />
            <button
              onClick={() => handleEventClick(selectedEvent)}
              style={{
                marginTop: '20px', 
                padding: '8px 16px',
                cursor: 'pointer',
                borderWidth: 2,
                borderColor: '#F48E28',
                borderRadius: '8px',
                color: '#F48E28',
                fontWeight: '800',
                fontSize: '18px',
                backgroundColor: 'rgba(255,255,255,0)',
              }}
            >
              Back to All Events
            </button>
          </div>
        </div>
      ) : (
        // List view of all events
        <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
          <h1 style={{ paddingTop: '100px', marginTop: 0, color: '#A68368' }}>Cafe Buzz</h1>
          <p style={{ padding: '10px', color: '#6C441A' }}>
            Think of this as your bulletin board for every cafe. Jump into the fun of cafe life where every moment brings people together with BeanBonus, like art nights and coffee tastings. Download the app to add your own event!
          </p>

          {Object.keys(groupedEvents).length > 0 ? (
            Object.entries(groupedEvents).map(([day, dayEvents]) => (
              <div key={day}>
                <h2 style={{ color: '#F48E28', padding: 5, paddingBottom: 10, marginBottom: 20, fontSize: '1.8em' }}>
                  {day}
                </h2>
                {dayEvents.map(event => (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '4px',
                      border: '2px solid #A68368',
                      borderRadius: '12px',
                      backgroundColor: '#f9f9f9',
                      marginBottom: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      maxWidth: '600px', 
                      marginLeft: 'auto',
                      marginRight: 'auto',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.border = '3px solid #F48E28')}
                    onMouseLeave={e => (e.currentTarget.style.border = '2px solid #A68368')}
                  >
                    <div style={{ marginRight: '16px', flexShrink: 0 }}>
                      {imageLoaded[event.id] ? (
                        <img
                          src={imageLoaded[event.id]}
                          alt={event.name}
                          style={{
                            width: '100px',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            console.error(`Image load failed for event ${event.id}`);
                          }}
                        />
                      ) : (
                        <PlaceholderImage eventId={event.id} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: '#F48E28', fontWeight: '900', textAlign: 'left', fontSize: 18, paddingRight: 5 }}>
                        {event.name}
                      </p>
                      <p style={{ color: '#6C441A', fontWeight: '400', textAlign: 'left', paddingRight: 5, fontSize: 12 }}>
                        {event.description.substring(0,60)}{"..."}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <p
                          style={{
                            color: '#A68368',
                            fontWeight: '800',
                            fontSize: 14,
                            textAlign: 'left',
                            margin: '0',
                          }}
                        >
                          <span role="img" aria-label="location">📍</span> {event.location.name || 'TBD'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <p>No upcoming events available. Check back soon!</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Events;